/**
 * SummaryCard - Enhetlig sammanfattningskort för rapporter
 * Titel, värde, valfri ikon och undertitel
 */

import React from 'react';

export type SummaryCardVariant = 'card' | 'elevated';

export interface SummaryCardProps {
  /** Etikett ovanför värdet */
  title: string;
  /** Huvudvärde (tal, text eller JSX t.ex. med ikon) */
  value: React.ReactNode;
  /** Valfri ikon (visas till vänster) */
  icon?: React.ReactNode;
  /** Om satt, wrappar ikonen i en låda med denna klass (t.ex. bg-red-100) */
  iconBoxClassName?: string;
  /** Valfri undertitel under värdet (t.ex. "av 12 fordon") */
  subtitle?: string;
  /** Klass på värdet (t.ex. text-green-600, text-red-600) */
  valueClassName?: string;
  /** card = bg-bgCard shadow, elevated = bg-bgElevated border */
  variant?: SummaryCardVariant;
  /** Ytterligare CSS-klasser på kortet */
  className?: string;
}

const VARIANT_CLASSES: Record<SummaryCardVariant, string> = {
  card: 'bg-bgCard rounded-lg p-4 shadow-sm',
  elevated: 'bg-bgElevated rounded-lg border border-border p-4',
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  iconBoxClassName,
  subtitle,
  valueClassName = 'text-textDefault',
  variant = 'card',
  className = '',
}) => {
  const content = (
    <>
      <p className="text-sm text-textSecondary">{title}</p>
      <div className={`text-2xl font-bold flex items-center gap-2 flex-wrap ${valueClassName}`}>
        {value}
      </div>
      {subtitle && (
        <div className="text-xs text-textSecondary mt-1">{subtitle}</div>
      )}
    </>
  );

  return (
    <div className={`${VARIANT_CLASSES[variant]} ${className}`}>
      {icon ? (
        <div className="flex items-center gap-3">
          {iconBoxClassName ? (
            <div className={`p-2 rounded-lg shrink-0 ${iconBoxClassName}`}>
              {icon}
            </div>
          ) : (
            <div className="shrink-0">{icon}</div>
          )}
          <div className="min-w-0">{content}</div>
        </div>
      ) : (
        content
      )}
    </div>
  );
};
